import Flickity from 'flickity';
import { getRTL, getBreakpoint } from './_utils';
import debounce from 'lodash/debounce';
import findIndex from 'lodash/findIndex';
import isEqual from 'lodash/isEqual';
const baseClass = 'QuickLinks';
const buttonDisabled = `${baseClass}-carousel-button--disabled`;
const cutoff = `${baseClass}-link-cutoff`;
const fullscreenHeroClass = 'FullscreenHero';
const quickLinkCellWidth = 145;

const isRTL = getRTL();

const selectors = {
	base: `.${baseClass}`,
	quickLinksInHero: `.${baseClass}--hero`,
	tablet: `.${baseClass}--tablet`,
	carouselContainer: `.${baseClass}-carousel-container`,
	carouselCell: `.carousel-cell`,
	nextButton: `.${baseClass}-carousel-button--next`,
	prevButton: `.${baseClass}-carousel-button--prev`,
	link: `.${baseClass}-link--container`,
	hero: `.${fullscreenHeroClass}.has-quick-links .Hero`,
	heroComponentsContainer: `.${fullscreenHeroClass}-container`,
	quickLinksBodyVersion: `.${fullscreenHeroClass} .${baseClass}--body`,
};

const initHeroQuickLinks = $quickLinks => {
	//Carousel for Tablet/Desktop
	const $carouselCells = $quickLinks.find(selectors.carouselCell);
	$quickLinks.find(selectors.carouselCell).removeClass('col-sm-6 col-md-2');
	const maxWidth = $carouselCells.length * quickLinkCellWidth + 80;
	$quickLinks.css('max-width', maxWidth);
	const $container = $quickLinks.find(selectors.carouselContainer);
	const $nextButton = $quickLinks.find(selectors.nextButton);
	const $prevButton = $quickLinks.find(selectors.prevButton);
	let bp = getBreakpoint();
	if ($container.length > 0) {
		const options = {
			cellAlign: isRTL ? 'right' : 'left',
			pageDots: false,
			prevNextButtons: false,
			rightToLeft: isRTL,
			wrapAround: false,
			selectedAttraction: 0.05,
			friction: 0.8,
			draggable: true,
			adaptiveHeight: false,
			resize: true,
			percentPosition: false,
			imagesLoaded: false,
			contain: true,
			on: {
				ready: function () {
					const flickity = this;
					$container.attr('tabindex', '-1');
					const $flickityViewport = $('.flickity-viewport');

					$carouselCells.on('focusin', e => {
						if (bp === 'medium') {
							const leftPos = $(e.target).offset().left;
							if (
								leftPos < 0 ||
								leftPos + quickLinkCellWidth > $(window).innerWidth()
							) {
								let focusedCellIndex = findIndex(flickity.cells, cell => {
									return isEqual($(cell.element).children('a').get(0), e.target);
								});
								flickity.select(focusedCellIndex);
							}
						}
					});

					const showButtons = index => {
						$nextButton.removeClass(buttonDisabled);
						$nextButton.prop('disabled', '');
						$prevButton.removeClass(buttonDisabled);
						$prevButton.prop('disabled', '');
						if (index === 0) {
							if ($prevButton.is(':focus-visible')) {
								$(flickity.selectedElement).children('a').trigger('focus');
							}
							$prevButton.addClass(buttonDisabled);
							$prevButton.prop('disabled', true);
						} else if (index === flickity.cells.length - 1) {
							if ($nextButton.is(':focus-visible')) {
								$(flickity.selectedElement).children('a').trigger('focus');
							}
							$nextButton.addClass(buttonDisabled);
							$nextButton.prop('disabled', true);
						}
					};

					let numberOfCompletelyVisibleCells, lastMovingIndex;
					const checkAvailableCellSpace = () => {
						//recalculate visible cells
						numberOfCompletelyVisibleCells = Math.floor(
							$flickityViewport.innerWidth() / quickLinkCellWidth
						);
						lastMovingIndex = flickity.cells.length - numberOfCompletelyVisibleCells;
						//hide arrows if we can see all the quick links
						if ($flickityViewport.innerWidth() >= flickity.slideableWidth - 15) {
							//showing all cells
							$nextButton.addClass(buttonDisabled);
							$prevButton.addClass(buttonDisabled);
							$nextButton.prop('disabled', true);
							$prevButton.prop('disabled', true);
							$carouselCells.removeAttr('aria-hidden');
						} else {
							showButtons(flickity.selectedIndex);
						}
					};
					checkAvailableCellSpace();
					$nextButton.on('click', function () {
						let targetIndex = flickity.selectedIndex + 1;
						if (
							targetIndex >= lastMovingIndex &&
							targetIndex <= flickity.cells.length
						) {
							flickity.select(flickity.cells.length - 1);
						} else {
							flickity.select(targetIndex);
						}
					});
					$prevButton.on('click', function () {
						let targetIndex = flickity.selectedIndex - 1;
						if (
							targetIndex >= lastMovingIndex &&
							targetIndex <= flickity.cells.length
						) {
							flickity.select(lastMovingIndex - 1);
						} else {
							flickity.select(targetIndex);
						}
					});

					flickity.on('change', showButtons);

					const setGradient = progress => {
						const isTablet = bp === 'medium';
						if (bp === 'large' || bp === 'xlarge' || isTablet) {
							flickity.cells.forEach(cell => {
								let $cell = $(cell.element);
								let $link = $cell.children(selectors.link);
								$link
									.removeClass(cutoff)
									.css('background', '')
									.attr('tabindex', '');
								$cell.removeAttr('aria-hidden');
								let leadingEdgePos =
									Number.parseFloat(
										$cell.css(isRTL ? 'right' : 'left').replace('px', '')
									) + 7.5;
								let trailingEdgePos = leadingEdgePos + quickLinkCellWidth - 15;
								if (leadingEdgePos < progress) {
									if (!isTablet) {
										let percentShowing =
											((quickLinkCellWidth -
												7.5 -
												(progress - leadingEdgePos)) /
												quickLinkCellWidth) *
											100;
										let gradientSettings = `linear-gradient(${
											isRTL ? '90' : '270'
										}deg, rgba(45, 45, 45, 1) ${
											percentShowing / 2
										}%, rgba(45, 45, 45, 0) ${percentShowing}%`;

										$link
											.addClass(cutoff)
											.css('background', gradientSettings)
											.attr('tabindex', '-1');
									}

									$cell.attr('aria-hidden', 'true');
								} else if (
									trailingEdgePos - $flickityViewport.innerWidth() >=
									progress
								) {
									if (!isTablet) {
										let percentShowing =
											((progress -
												(leadingEdgePos - $flickityViewport.innerWidth())) /
												quickLinkCellWidth) *
											100;
										let gradientSettings = `linear-gradient(${
											isRTL ? '270' : '90'
										}deg, rgba(45, 45, 45, 1) ${
											percentShowing / 2
										}%, rgba(45, 45, 45, 0) ${percentShowing}%`;
										$link
											.addClass(cutoff)
											.css('background', gradientSettings)
											.attr('tabindex', '-1');
									}
									$cell.attr('aria-hidden', 'true');
								}
							});
						} else {
							$(selectors.link)
								.removeClass(cutoff)
								.css('background', '')
								.attr('tabindex', '');
						}
					};

					setGradient(0);
					let lastProgress = 0;
					flickity.on('scroll', (event, progress) => {
						lastProgress - progress;
						setGradient(progress);
					});
					flickity.on('change', () => {
						setGradient(lastProgress);
					});

					$(window).on(
						'resize',
						debounce(
							function () {
								checkAvailableCellSpace();
								bp = getBreakpoint();
								setGradient(flickity.x * -1);
							},
							{ leading: false, trailing: true },
							200
						)
					);
				},
			},
		};
		$container.addClass(`${baseClass}--flickity`);
		const flickity = new Flickity($container.get(0), options);

		return flickity;
	}
};

const initTabletCarousel = $quickLinks => {
	const $container = $quickLinks.find(selectors.carouselContainer);
	if ($container.length > 0) {
		const options = {
			cellAlign: 'left',
			pageDots: false,
			prevNextButtons: false,
			rightToLeft: getRTL(),
			wrapAround: false,
			selectedAttraction: 0.05,
			friction: 0.8,
			draggable: true,
			adaptiveHeight: false,
			resize: true,
			percentPosition: false,
			imagesLoaded: false,
			contain: true,
			on: {
				ready: function () {
					const flickity = this;
					const $flickityViewport = $('.flickity-viewport');

					$container.addClass(`${baseClass}--flickity`);
					$container.attr('tabindex', '-1');

					const $carouselCells = $quickLinks.find(selectors.carouselCell);
					$carouselCells.on('focusin', e => {
						const leftPos = $(e.target).offset().left;
						if (leftPos < 0 || leftPos + quickLinkCellWidth > $(window).innerWidth()) {
							let focusedCellIndex = findIndex(flickity.cells, cell => {
								return isEqual($(cell.element).children('a').get(0), e.target);
							});
							flickity.select(focusedCellIndex);
						}
					});
					const fixAria = () => {
						let progress = flickity.x * -1;
						flickity.cells.forEach(cell => {
							let $cell = $(cell.element);
							$cell.removeAttr('aria-hidden');
							let leadingEdgePos =
								Number.parseFloat(
									$cell.css(isRTL ? 'right' : 'left').replace('px', '')
								) + 7.5;
							let trailingEdgePos = leadingEdgePos + quickLinkCellWidth - 15;
							if (leadingEdgePos < progress) {
								$cell.attr('aria-hidden', 'true');
							} else if (
								trailingEdgePos - $flickityViewport.innerWidth() >=
								progress
							) {
								$cell.attr('aria-hidden', 'true');
							}
						});
					};

					fixAria();
					flickity.on('scroll', () => {
						fixAria();
					});
					flickity.on('change', () => {
						fixAria();
					});

					$(window).on(
						'resize',
						debounce(fixAria, { leading: false, trailing: true }, 200)
					);
				},
			},
		};

		const flickity = new Flickity($container.get(0), options);

		return flickity;
	}
};

export default function initQuickLinks() {
	$(selectors.quickLinksInHero).each((i, quickLinks) => {
		initHeroQuickLinks($(quickLinks));
	});

	$(selectors.tablet).each((i, quickLinks) => {
		initTabletCarousel($(quickLinks));
	});
}
